import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, } from 'react'
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom'
import SubscriptionFeature from '@components/marketing/SubscriptionFeatureModal'
import useMapDataContext, { MapData } from '@contexts/mapDataContext'
import RegisterUserModal from '@components/auth/RegisterUserModal'
import NewPostForm from '@forms/new_post_form/NewPostForm'
import NearbyProfile from '@screens/NearbyProfile'
import MediaViewer from '@screens/MediaViewer'
import Messages from '@screens/Messages'

const ModalContext = createContext();
const ModalProvider = ({ children }) => {
  const { setNavigateFuncIsSet } = useMapDataContext()
  const addNewPostToFeedsRef = useRef([])
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const setNewPostCallbackRef = (callback) => {
    addNewPostToFeedsRef.current.push(callback)
  }

  const callNewPostsCallbacks = (newPost) => {
    addNewPostToFeedsRef.current?.forEach((callback) => {
      if (typeof callback === 'function') callback(newPost)
    })
  }
  
  // Add screen modal locations here
  const screenModals = {
    newPost: 'new-post',
    messagesModal: 'messages', // accepts ?conversation_id
    mediaViewer: 'media-viewer', // accepts: ?media_type: ['image', 'video']
    nearbyProfile: 'nearby-profile', // takes user id in state rather than in a route. We don't want users navigating here directly.
  }

  // Add action modal locations here
  const actionModals = {
    subscriptionFeature: 'subscription-feature',
    registerUser: 'register-user',
    exitApp: 'exit-app',
  }

  const modalCallbacks = useRef({
    'new-post': {},
    'messages': {},
    'media-viewer': {},
    'nearby-profile': {},
    'subscription-feature': {},
    'register-user': {},
  })

  function modalUtilities(configHash, queryKey, utilOptions={ replace: false }) {
    return Object.entries(configHash).reduce((object, [key, value]) => {
      object[key] = {
        callbacks: modalCallbacks.current[value],
        isOpen: searchParams.has(queryKey, value),
        open: function(options={}, state={}, callbacks={}) {
          options = options ? options : {}
          let oldParams = {}
          if (!utilOptions.replace) {
            oldParams = searchParams.entries().reduce((acc, [key, val]) => {
              acc[key] = val
              return acc
            }, {})
          }

          // NOTE: We're setting callbacks here because the native state functionality only accepts
          //       objects that can be serialized to json.
          modalCallbacks.current[value] = callbacks

          // Set path to current path if no pathname specified, since apparently that isn't necessarily a given, and the path sometimes gets set to other routes...
          if (!options.pathname) options.pathname = window?.location?.pathname

          // Set the query params to be properly used by the router
          options.search = createSearchParams({
            ...oldParams,
            [queryKey]: value,
            ...options.search
          }).toString()

          // Navigate to destination
          navigate(options, state)
        },
      }
      return object
    }, {})
  }

  const screenModalUtilities = modalUtilities(screenModals, 'screen_modal', { replace: true })
  const actionModalUtilities = modalUtilities(actionModals, 'action_modal')
  
  // We need to set the navigation function for the map data so the user can route to the nearby profiles
  useLayoutEffect(() => {
    if (!MapData.navigateFunc.value) {
      MapData.navigateFunc.value = screenModalUtilities.nearbyProfile.open
      setNavigateFuncIsSet(true)
    }
  }, [screenModalUtilities.nearbyProfile])

  return (
    <ModalContext.Provider value={{ ...screenModalUtilities, ...actionModalUtilities, setNewPostCallbackRef, callNewPostsCallbacks }}>
      {children}
      <NewPostForm />
      <Messages />
      <MediaViewer mediaViewer={screenModalUtilities?.mediaViewer} />
      <NearbyProfile />
      <SubscriptionFeature />
      <RegisterUserModal />
    </ModalContext.Provider>
  )
}

const useModalContext = () => useContext(ModalContext)

export default useModalContext

export { ModalContext, ModalProvider, useModalContext }
